import { zodResolver } from '@hookform/resolvers/zod';
import { createPaymentBodySchema, type CreatePaymentBody } from '@klopotun/contracts';
import type { FormEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Button } from '../../../../shared/ui/index.js';
import { mergeHistory, pastDueDatesOf } from '../../lib/past-payments.js';
import { recurrenceRuleOfValues, toPaymentBodyDraft } from '../../lib/payment-form-adapter.js';
import { paymentFormSchema } from '../../lib/payment-form-schema.js';
import type {
  PastPaymentDraft,
  PaymentFormStep,
  PaymentFormValues,
} from '../../model/payment-form-values.js';
import { DetailsStep } from '../details-step.js';
import { HistoryStep } from '../history-step/history-step.js';
import { ScheduleStep } from '../schedule-step.js';
import styles from './payment-form.module.css';

const MAIN_FIELDS: (keyof PaymentFormValues)[] = [
  'name',
  'amount',
  'currency',
  'isVariableAmount',
  'kind',
  'frequency',
  'customIntervalUnit',
  'customIntervalCount',
  'startDate',
  'cardId',
  'categoryId',
  'endDate',
  'cancelUrl',
  'note',
];

/**
 * The past dates step one describes, and only when there is a past to fill in: a creation (it is
 * the only caller that knows today), a fixed amount to prefill, and a first charge behind today.
 */
const pastDueDatesFor = (values: PaymentFormValues, today: string | undefined): string[] => {
  const rule = recurrenceRuleOfValues(values);
  const canFillHistory = today !== undefined && !values.isVariableAmount && rule !== null;

  if (!canFillHistory) {
    return [];
  }

  return pastDueDatesOf(rule, today);
};

const historyFor = (values: PaymentFormValues, today: string | undefined): PastPaymentDraft[] => {
  if (today === undefined) {
    return [];
  }

  return mergeHistory({ existing: values.history, dueDates: pastDueDatesFor(values, today) });
};

type PaymentFormProps = {
  initialValues: PaymentFormValues;
  step: PaymentFormStep;
  onStepChange: (step: PaymentFormStep) => void;
  onSubmit: (body: CreatePaymentBody) => void;
  busy: boolean;
  /** Today as YYYY-MM-DD; passed only by the create screen, which may fill in a past start. */
  today?: string;
};

export const PaymentForm = ({
  initialValues,
  step,
  onStepChange,
  onSubmit,
  busy,
  today,
}: PaymentFormProps) => {
  const { control, handleSubmit, trigger, getValues, setValue } = useForm<PaymentFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(paymentFormSchema),
    mode: 'onTouched',
  });
  const [startDate, isVariableAmount] = useWatch({
    control,
    name: ['startDate', 'isVariableAmount'],
  });
  const hasHistoryStep =
    today !== undefined && !isVariableAmount && startDate.trim() !== '' && startDate.trim() < today;

  const save = handleSubmit((values) => {
    onSubmit(createPaymentBodySchema.parse(toPaymentBodyDraft(values)));
  });

  const goToHistory = async (): Promise<void> => {
    const isMainValid = await trigger(MAIN_FIELDS);

    if (!isMainValid) {
      return;
    }

    const history = historyFor(getValues(), today);

    setValue('history', history);

    if (history.length > 0) {
      onStepChange('history');
      return;
    }

    void save();
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    if (step === 'main' && hasHistoryStep) {
      event.preventDefault();
      void goToHistory();
      return;
    }

    void save(event);
  };

  return (
    <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
      {step === 'history' && <p className={styles.stepLabel}>Минулі платежі</p>}

      <div className={styles.fields}>
        {step === 'main' && <ScheduleStep control={control} />}
        {step === 'main' && <DetailsStep control={control} />}
        {step === 'history' && <HistoryStep control={control} />}
      </div>

      <div className={styles.actions}>
        {step === 'main' && hasHistoryStep ? (
          <Button
            key="next"
            type="button"
            onClick={() => {
              void goToHistory();
            }}
          >
            Далі
          </Button>
        ) : (
          <Button key="save" type="submit" busy={busy}>
            Зберегти
          </Button>
        )}
      </div>
    </form>
  );
};
